import React, { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Button,
  CircularProgress,
  Alert,
  Paper,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  LinearProgress,
} from '@mui/material';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { generateQuizForCourse } from '../store/thunks/quizThunks';
import { setQuestionIndex, setAnswer, setResult } from '@e-learning/shared/src/store/slices/quizSlice';
import { calculateQuizResult } from '@e-learning/shared/src/quiz/quizGenerator';
import { trackQuizStarted } from '../services/analytics/firebaseAnalytics';

const QuizPage: React.FC = () => {
  const { courseId } = useParams<{ courseId: string }>();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { currentQuiz, currentQuestionIndex, answers, loading, error } = useAppSelector((state) => state.quiz);
  const { user, isAuthenticated } = useAppSelector((state) => state.auth);

  useEffect(() => {
    if (courseId && (!currentQuiz || currentQuiz.courseId !== courseId)) {
      dispatch(generateQuizForCourse({ courseId, isAuthenticated })).then((result) => {
        if (result.type === 'quiz/generate/fulfilled' && result.payload) {
          trackQuizStarted(courseId, result.payload.id);
        }
      });
    }
  }, [courseId, dispatch, currentQuiz, isAuthenticated]);

  if (loading) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error || !currentQuiz) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ my: 4 }}>
          <Alert severity="error">{error || 'Failed to load quiz'}</Alert>
          <Button onClick={() => navigate(`/courses/${courseId}`)} sx={{ mt: 2 }}>
            Back to Course
          </Button>
        </Box>
      </Container>
    );
  }

  const currentQuestion = currentQuiz.questions[currentQuestionIndex];
  const totalQuestions = currentQuiz.questions.length;
  const progress = ((currentQuestionIndex + 1) / totalQuestions) * 100;
  const selectedAnswer = answers[currentQuestion.id];

  const handleAnswerChange = (answerIndex: number) => {
    dispatch(setAnswer({ questionId: currentQuestion.id, answerIndex }));
  };

  const handlePrevious = () => {
    if (currentQuestionIndex > 0) {
      dispatch(setQuestionIndex(currentQuestionIndex - 1));
    }
  };

  const handleNext = () => {
    if (currentQuestionIndex < totalQuestions - 1) {
      dispatch(setQuestionIndex(currentQuestionIndex + 1));
    }
  };

  const handleSubmit = () => {
    const result = calculateQuizResult(currentQuiz, answers, user?.uid || null);
    const quizResult = {
      id: `result_${Date.now()}`,
      userId: user?.uid || null,
      courseId: currentQuiz.courseId,
      quizId: currentQuiz.id,
      score: result.score,
      totalQuestions: result.totalQuestions,
      correctCount: result.correctCount,
      questions: result.questions,
      timestamp: new Date().toISOString(),
      completionStatus: 'completed' as const,
    };
    dispatch(setResult(quizResult));
    navigate(`/courses/${courseId}/quiz/results`);
  };

  const isLastQuestion = currentQuestionIndex === totalQuestions - 1;
  const allQuestionsAnswered = currentQuiz.questions.every((q) => answers[q.id] !== undefined);

  return (
    <Container maxWidth="md">
      <Box sx={{ my: 4 }}>
        <Box sx={{ mb: 3 }}>
          <Typography variant="h5" gutterBottom>
            Quiz: Question {currentQuestionIndex + 1} of {totalQuestions}
          </Typography>
          <LinearProgress variant="determinate" value={progress} sx={{ mt: 2, height: 8, borderRadius: 4 }} />
        </Box>

        <Paper elevation={2} sx={{ p: 4 }}>
          <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
            {currentQuestion.questionText}
          </Typography>

          <FormControl component="fieldset" fullWidth>
            <RadioGroup
              value={selectedAnswer !== undefined ? selectedAnswer.toString() : ''}
              onChange={(e) => handleAnswerChange(parseInt(e.target.value))}
            >
              {currentQuestion.options.map((option, index) => (
                <FormControlLabel
                  key={index}
                  value={index.toString()}
                  control={<Radio />}
                  label={option}
                  sx={{ mb: 1, p: 1, borderRadius: 1, '&:hover': { bgcolor: 'action.hover' } }}
                />
              ))}
            </RadioGroup>
          </FormControl>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
            <Button
              variant="outlined"
              onClick={handlePrevious}
              disabled={currentQuestionIndex === 0}
            >
              Previous
            </Button>
            {isLastQuestion ? (
              <Button
                variant="contained"
                onClick={handleSubmit}
                disabled={!allQuestionsAnswered}
              >
                Submit Quiz
              </Button>
            ) : (
              <Button
                variant="contained"
                onClick={handleNext}
                disabled={selectedAnswer === undefined}
              >
                Next
              </Button>
            )}
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default QuizPage;

